import { CoreError } from './errors.js';

const DECIMAL_PATTERN = /^(\d+)(?:\.(\d+))?$/;

/**
 * Parses a non-negative decimal string into a scaled integer without ever
 * touching floating point. `parseScaled("12.5", 2)` → `1250n`.
 *
 * Rejects values with more fractional digits than `scale`, so "0.001 USD"
 * is an error rather than being silently rounded.
 */
export function parseScaled(input: string | number, scale: number): bigint {
  const text = typeof input === 'number' ? numberToPlainString(input) : input.trim();
  const match = DECIMAL_PATTERN.exec(text);
  if (!match) {
    throw new CoreError('INVALID_AMOUNT', `"${String(input)}" is not a valid non-negative number`);
  }
  const whole = match[1] ?? '0';
  const fraction = match[2] ?? '';
  if (fraction.length > scale) {
    throw new CoreError(
      'INVALID_AMOUNT',
      `"${String(input)}" has more than ${scale} decimal place${scale === 1 ? '' : 's'}`,
    );
  }
  return BigInt(whole + fraction.padEnd(scale, '0'));
}

/** Formats a scaled integer back into a plain decimal string: `(1250n, 2)` → `"12.50"`. */
export function formatScaled(value: bigint | number, scale: number): string {
  const big = BigInt(value);
  const negative = big < 0n;
  const digits = (negative ? -big : big).toString().padStart(scale + 1, '0');
  const whole = scale === 0 ? digits : digits.slice(0, -scale);
  const fraction = scale === 0 ? '' : `.${digits.slice(-scale)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

function numberToPlainString(value: number): string {
  if (!Number.isFinite(value) || value < 0) {
    throw new CoreError('INVALID_AMOUNT', `${value} is not a valid non-negative number`);
  }
  // Avoid exponent notation (1e21) and binary artefacts (0.1 + 0.2).
  return value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
}

/** Converts a bigint that is known to be small back to a JS number, guarding precision. */
export function toSafeNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new CoreError('INVALID_AMOUNT', 'Amount is too large');
  }
  return Number(value);
}
